import { hashSecret, verifySecret, type AuthUser } from '@pms/shared';

import { db } from '../sync/db.js';

/**
 * Память устройства о тех, кто на нём уже входил.
 *
 * Нужна ровно для одного: закрыть смену в цеху, где связи может не быть неделю.
 * Хэш пароля считает само устройство при успешном входе ПРИ СЕТИ — с сервера
 * не приезжает ни хэш, ни логин. Раньше на каждый планшет рассылались хэши PIN
 * всех операторов сразу, и устройство у одного агрегата хранило ключи от
 * учётных записей всего цеха.
 *
 * Следствие, которое нужно понимать: первый вход каждого человека на новом
 * устройстве возможен только при связи.
 */

export interface CachedCredential {
  /** Ключ таблицы. */
  login: string;
  userId: string;
  fullName: string;
  role: string;
  /** PBKDF2 от пароля, посчитанный здесь же, на устройстве. */
  hash: string;
  updatedAt: string;
}

export async function rememberCredential(login: string, password: string, user: AuthUser): Promise<void> {
  await db.credentials.put({
    login,
    userId: user.id,
    fullName: user.fullName,
    role: user.role,
    hash: await hashSecret(password),
    updatedAt: new Date().toISOString(),
  });
}

export async function findCredential(login: string): Promise<CachedCredential | undefined> {
  return db.credentials.get(login);
}

/**
 * Проверка пароля без сети. Возвращает того, кто вошёл, или null.
 *
 * Если администратор сменил пароль, здесь останется старый — но сессии при
 * смене пароля закрываются, поэтому при первом же выходе на связь понадобится
 * вход заново, и запись перезапишется.
 */
export async function verifyOffline(login: string, password: string): Promise<CachedCredential | null> {
  const stored = await findCredential(login);
  if (!stored) return null;
  return (await verifySecret(password, stored.hash)) ? stored : null;
}

/** При отключении сотрудника или смене устройства запись можно убрать. */
export async function forgetCredential(login: string): Promise<void> {
  await db.credentials.delete(login);
}
